import BrandMark from "./BrandMark";
import React, { useEffect, useRef, useState } from "react";
import ProveDecisionGraph from "../audit/ProveDecisionGraph";
import AmendmentPdfViewerModal from "../audit/AmendmentPdfViewer";
import { LOGINHS_NOTICES } from "./NewsPage";
import { mergeNewsItems } from "./newsFeed";
import AmendmentNoticeModal from "./AmendmentNoticeModal";
import {
  amendmentNoticeToNewsItem,
  fetchRecentAmendmentNotices,
  useLatestAmendmentNotice,
} from "./amendmentNotice";
import {
  ArrowRight,
  Layers,
  Brain,
  Bot,
  ScrollText,
  Calculator,
  ShieldCheck,
  Search,
  ChevronRight,
  TriangleAlert,
  ExternalLink,
  FileText, Smartphone } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// loginHS Landing — HS 세번(품목분류) 전문 조회 SaaS
//   스타일: Ponder 식 절제 톤 — 거의 흑백(near-black 버튼·헤딩·통계) + 속삭이듯
//           옅은 라벤더/쿨그레이 틴트만. 고채도 색을 피하고 여백·타이포·제품
//           스크린샷으로 프리미엄 느낌을 낸다. (라벤더는 배경/뱃지에만 미세하게)
// ─────────────────────────────────────────────────────────────

const INK = "#16151a"; // 헤딩·헤드라인
const DARK = "#1b1a20"; // primary 버튼 (near-black, Ponder "사고 시작" 톤)
const BODY = "#4b5563"; // 본문
const MUTED = "#6b7280"; // 보조
const LINE = "#ecedf2"; // 중립 보더
const BG_SOFT = "#fafbfd"; // 옅은 카드 배경
const TINT = "#f4f6fc"; // 섹션 pale cool tint
const TINT_DEEP = "#eceffb"; // 히어로 backdrop / 제품 프레임
const SOFT_BG = "#f1effa"; // 라벤더 뱃지 배경(저채도)
const SOFT_TX = "#6a6593"; // 라벤더-그레이 텍스트(뱃지·라벨·아이콘)
const SOFT_LINE = "#e6e6f2"; // 라벤더 톤 보더(미세)

// 히어로 제품 스크린샷 — public/landing/hero-classify.png 에 실제 캡처를 넣으세요.
const HERO_SHOT = (process.env.PUBLIC_URL || "") + "/landing/hero-classify.png";
// 히어로 데모 영상(2026-07-04) — 분류 실플로우 녹화(mp4, 무음 루프). 교체 시 같은 파일명으로.
const DEMO_MP4 = (process.env.PUBLIC_URL || "") + "/landing/demo-classify.mp4";

const MOTOR_PRECEDENT_URL =
  "https://unipass.customs.go.kr/clip/prlstclsfsrch/openULS0203003S.do?rrdcNo=0072020000606";
const MOTOR_AMENDMENT_EVIDENCE = Object.freeze({
  seqNo: 484,
  sourcePage: 323,
  gosiNo: "2022-21",
  title: "MOTOR; NTWC021S02에 대한 품목분류 변경고시",
});

const FEATURES = [
  {
    icon: Layers,
    title: "세번 계층 조회",
    en: "Tariff Hierarchy",
    desc: "부→류→호→소호→세번(10자리) 완전 계층. 관세율표·국내세분·관세청 007Q 원문 링크까지 한 화면에서.",
  },
  {
    icon: Bot,
    title: "AI 모드 — 물어보면 답합니다",
    en: "AI Mode (Grounded Q&A)",
    desc: "\"방사선 방호복 세번은?\"처럼 자연어로 물어보면, 현재 분류 후보와 관세율표·분류사례·법령 근거를 인용해 대화형으로 답하고 관련 질문까지 제안합니다.",
  },
  {
    icon: Brain,
    title: "증거 기반 AI 분류",
    en: "Evidence-first Classification",
    desc: "품명과 상품 설명으로 후보를 찾고, 필요한 사실을 확인합니다. PROVE가 검수된 규칙으로 경로를 계산하며 사실이 부족하면 추가 질문을 남깁니다.",
  },
  {
    icon: ScrollText,
    title: "분류사례·법령·호해설",
    en: "Precedents & Law",
    desc: "분류사례 570,000건(관세청 58,000 + 미국·EU·일본·영국 등 해외 510,000, 국내는 결정사유 전문 포함) + 법령 356,000건 + 부주·류주·호해설을 의미(semantic) 검색으로 즉시 근거 확인.",
  },
  {
    icon: Calculator,
    title: "원산지별 적용세율 추천",
    en: "FTA Rate Finder",
    desc: "원산지 국가를 선택하면 기본·WTO·FTA·특혜세율 중 적용 가능한 것을 가려내고 가장 유리한(최저) 세율을 근거와 함께 추천합니다.",
  },
];

// 데이터 신뢰성 — 각 기능의 실제 출처·갱신 방식(코드 검증 기반, 2026-07-16). 과장 없이 정직하게.
//   ⚠ "실시간 전체 동기화"·"수출입은행 API"·"직통전화"는 근거 부족이라 쓰지 않음.
const TRUST_ITEMS = [
  {
    tag: "관세청 실시간 연동",
    title: "감면·환급",
    desc: "세번을 조회하면 관세청 CLIP에서 감면·환급 정보를 그 자리에서 직접 가져와 보여줍니다.",
  },
  {
    tag: "매일 갱신",
    title: "품목분류 사례",
    desc: "관세청의 신규 품목분류 사례를 매일 새벽 자동으로 반영합니다.",
  },
  {
    tag: "관세청 원문 기반",
    title: "관세율 · 요건 · 원산지",
    desc: "기본·WTO·FTA·할당관세와 수출입 요건, 원산지표시는 관세청 UNIPASS 원문을 기준으로 합니다.",
  },
  {
    tag: "법령 원문 연계",
    title: "근거 조문 링크",
    desc: "관세법 등 근거 조문은 국가법령정보(law.go.kr) 원문으로 바로 연결됩니다.",
  },
];

// 통계 — 규모(숫자) + 출처(데이터 권위)를 한 지표에 통합(별도 권위 칩과 중복 제거).
const STATS = [
  {
    value: "570,000+",
    label: "분류사례 (국내+해외)",
    source: "관세청 + 미국·EU·일본·영국 등 14개국",
  },
  {
    value: "356,000+",
    label: "관련 법령·요건",
    source: "관세법령정보포털 CLIP",
  },
  {
    value: "17,000+",
    label: "현행 세번·계층 데이터",
    source: "UNIPASS 관세율표 · 부→류→호→소호→세번",
  },
  {
    value: "1,230/1,230",
    label: "PROVE 프레임 v3",
    source: "HSK 계층 고정 · 2개 모델 독립 교차검증",
  },
];

// 하단 쇼케이스 4컬럼 (Ponder 식 — 윗줄 구분선 + 제목 + 본문)
const SHOWCASE_COLS = [
  {
    title: "한 줄로 시작",
    desc: "품명을 넣으면 부→류→호→소호→세번(10자리) 후보까지 이어서 보여줍니다.",
  },
  {
    title: "근거가 보이는 분류",
    desc: "분류사례·부주/류주·호해설을 같은 화면에서 즉시 확인합니다.",
  },
  {
    title: "검증된 갈림 질문",
    desc: "공식 코드 경로는 바꾸지 않고, 5,358개 결정 지점의 재질·용도·가공상태 질문을 독립 모델로 교차검증했습니다.",
  },
  {
    title: "세율·FTA까지",
    desc: "원산지별 적용 가능 세율 중 가장 유리한 것을 근거와 함께 추천합니다.",
  },
];

// 세그먼트 3분기 — 관세사/수출입기업/포워더·특송 (마케팅 전략 2026-07-04: 같은 엔진, 다른 쓰임새)
const SEGMENTS = [
  {
    icon: ShieldCheck,
    badge: "관세사·관세법인",
    title: "대체가 아니라, 무장",
    desc: "오분류 책임은 결국 관세사가 집니다. 그래서 근거 없는 AI는 쓸 수 없습니다. loginHS는 후보마다 관세율표·통칙·주·분류사례를 근거로 붙이고, 신고이력은 참고자료로 연결합니다. 확신이 없으면 갈림 질문을 던집니다 — 검토 시간은 줄고, 근거는 남습니다.",
    points: [
      "분류 검토에 관세율표·통칙·주·분류사례 자동 첨부",
      "갈림 축 질문으로 확정 전 확인",
      "의견서 초안(OpinionDraft) 지원",
    ],
    cta: { label: "파일럿 문의", action: "contact" },
  },
  {
    icon: Search,
    badge: "수출입기업",
    title: "HS코드, 틀리면 가산세",
    desc: "품명만 넣으면 세번과 근거를 함께 받습니다. 환율·세금계산기·특송운임 같은 실무 도구는 무료 — 분류가 헷갈리는 품목일수록 실전 함정 사례연구를 먼저 확인하세요.",
    points: [
      "품명 한 줄 → 세번 + 근거",
      "무료 도구: 환율·세금계산기·서식",
      "실전 함정 사례연구 공개",
    ],
    cta: { label: "사례연구 보기", action: "cases" },
  },
  {
    icon: Layers,
    badge: "포워더·특송사",
    title: "수천 건은 API로",
    desc: "목록통관·일반통관 구분처럼 반복되는 대량 분류는 Bulk API로 처리하고, review 플래그가 붙은 위험 건만 사람이 봅니다. API 키는 즉시 자가 발급됩니다.",
    points: [
      "Bulk 분류 API + 근거 동봉",
      "위험 건만 review 플래그",
      "API 키 자가 발급·사용량 조회",
    ],
    cta: { label: "API 문서 보기", action: "api-docs" },
  },
];

// 뉴스레터 구독 — listmonk 공개 폼(nl.loginhs.co.kr/subscription/form)에 숨은 iframe POST.
//   크로스오리진이지만 폼 POST는 허용(응답은 안 읽음) → CORS 불필요. 단일 옵트인 즉시 구독.
const NL_FORM_ACTION = "https://nl.loginhs.co.kr/subscription/form";
const NL_LIST_UUID = "a8388507-0919-4b2c-8bb7-7cf55c13efa7"; // loginHS 관세 실무 뉴스레터
const NewsletterSignup = ({ container }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const formRef = React.useRef(null);
  const submit = (e) => {
    // React 18 discrete-event 동기 flush 로 폼이 언마운트되며 native 제출이 유실되는
    // 레이스 방지 — preventDefault 후 ref 로 명시적 POST(hidden iframe)한 뒤 상태 전환.
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    if (formRef.current) formRef.current.submit();
    setDone(true);
  };
  return (
    <section style={{ borderTop: `1px solid ${LINE}`, padding: "56px 0" }}>
      <div style={{ ...container, textAlign: "center" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            background: SOFT_BG,
            color: SOFT_TX,
            border: `1px solid ${SOFT_LINE}`,
            borderRadius: 999,
            padding: "4px 12px",
            fontSize: 12.5,
            fontWeight: 700,
            marginBottom: 14,
          }}
        >
          뉴스레터
        </div>
        <h2
          style={{
            fontSize: 26,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: INK,
            margin: 0,
          }}
        >
          이 세번, 왜? — 매주 함정 사례를 근거와 함께
        </h2>
        <p
          style={{
            fontSize: 15,
            color: MUTED,
            margin: "10px auto 22px",
            maxWidth: 560,
            lineHeight: 1.6,
          }}
        >
          세번분류에서 실제로 틀리기 쉬운 함정 사례와 관세·무역 실무 소식을 정기적으로
          보내드립니다. 무료이며 언제든 수신거부할 수 있습니다.
        </p>

        {done ? (
          <p style={{ fontSize: 15, fontWeight: 700, color: "#1e6b34" }}>
            ✓ 구독 신청이 완료되었습니다. 감사합니다.
          </p>
        ) : (
          <form
            ref={formRef}
            action={NL_FORM_ACTION}
            method="post"
            target="nl_signup_iframe"
            onSubmit={submit}
            style={{
              display: "flex",
              gap: 8,
              maxWidth: 460,
              margin: "0 auto",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <input type="hidden" name="l" value={NL_LIST_UUID} />
            <input type="hidden" name="name" value="" />
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일 주소"
              required
              style={{
                flex: "1 1 240px",
                minWidth: 0,
                padding: "12px 14px",
                fontSize: 15,
                border: `1px solid ${LINE}`,
                borderRadius: 10,
                outline: "none",
              }}
            />
            <button
              type="submit"
              style={{
                background: DARK,
                color: "#fff",
                border: "none",
                borderRadius: 10,
                padding: "12px 22px",
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              구독하기
            </button>
          </form>
        )}
        <p style={{ fontSize: 12, color: "#a2a6b3", marginTop: 12 }}>
          구독 시 마케팅·홍보 정보 수신에 동의하는 것으로 간주됩니다. 처리방침을 따릅니다.
        </p>
        {/* 숨은 iframe — 폼 POST 응답을 여기로 받아 페이지 이동 방지 */}
        <iframe
          name="nl_signup_iframe"
          title="newsletter-signup"
          style={{ display: "none" }}
        />
      </div>
    </section>
  );
};

// 관세청 공지 스트립 — 최신 공지를 통합 정렬, 더보기 → ?page=news
const LandingNewsStrip = ({ container }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    Promise.allSettled([
      fetchRecentAmendmentNotices(5),
      fetch("/api/onto/customs-notices").then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([amendmentResult, noticeResult]) => {
        const amendments = amendmentResult.status === "fulfilled"
          ? amendmentResult.value.map(amendmentNoticeToNewsItem).filter(Boolean)
          : [];
        const notices = noticeResult.status === "fulfilled" ? noticeResult.value?.items || [] : [];
        setItems(mergeNewsItems(amendments, LOGINHS_NOTICES, notices).slice(0, 5));
      })
      .catch(() => setItems(mergeNewsItems(LOGINHS_NOTICES)));
  }, []);
  if (!items || items.length === 0) return null; // 로딩/실패 시 조용히 생략
  const go = (e) => {
    e.preventDefault();
    window.history.pushState({}, "", "/?page=news");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  return (
    <section style={{ borderTop: `1px solid ${LINE}`, padding: "40px 0" }}>
      <div style={container}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            marginBottom: 14,
          }}
        >
          <h2 style={{ fontSize: 20, fontWeight: 800, color: INK, margin: 0 }}>
            새소식
          </h2>
          <span style={{ fontSize: 13, color: MUTED }}>관세청 공지</span>
          <a
            href="/?page=news"
            onClick={go}
            style={{
              marginLeft: "auto",
              fontSize: 13.5,
              fontWeight: 600,
              color: INK,
              textDecoration: "none",
            }}
          >
            전체 보기 →
          </a>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          {items.map((x, i) => (
            <a
              key={x.id || i}
              href="/?page=news"
              onClick={go}
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 12,
                padding: "9px 2px",
                borderBottom: i < items.length - 1 ? `1px solid ${LINE}` : "none",
                textDecoration: "none",
              }}
            >
              <span
                style={{
                  fontSize: 12.5,
                  color: MUTED,
                  fontFamily: "JetBrains Mono, monospace",
                  flexShrink: 0,
                }}
              >
                {x.date}
              </span>
              <span
                style={{
                  fontSize: 14,
                  color: BODY,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {x.title}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const LandingPageLoginHS = ({ onLoginClick }) => {
  const [shown, setShown] = useState(false);
  const [amendmentPdf, setAmendmentPdf] = useState(null);
  const latestAmendmentNotice = useLatestAmendmentNotice();
  // 모바일 — 네비 앵커(기능/PROVE/데이터)가 좁은 폭에서 세로로 꺾여 깨짐 → 숨김
  const [vw, setVw] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1280,
  );
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobileNav = vw < 640;
  const [heroImgOk, setHeroImgOk] = useState(true);
  // 비로그인 사용자는 상단에서, 로그인 사용자는 사이드바에서 같은 실무 도구에 접근한다.
  const [guideOpen, setGuideOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const guideMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const NAV_GUIDES = [
    ["사용자 가이드", "/?page=guide"],
    ["모바일 앱 설치", "/?page=install"],
    ["API 문서", "/?page=api-docs"],
  ];
  const NAV_TOOLS = [
    ["관세환율", "/?page=exchange"],
    ["특송운임", "/?page=freight"],
    ["세금 계산기", "/?page=taxcalc"],
    ["서식자료실", "/?page=forms"],
    ["무역통계부호", "/?page=trade-codes"],
    ["요건확인기관 연락처", "/?page=req-agencies"],
  ];
  const goPage = (href) => {
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  useEffect(() => {
    if (!guideOpen && !toolsOpen) return undefined;
    const closeOutside = (event) => {
      const inGuide = guideMenuRef.current?.contains(event.target);
      const inTools = toolsMenuRef.current?.contains(event.target);
      if (!inGuide && !inTools) {
        setGuideOpen(false);
        setToolsOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setGuideOpen(false);
        setToolsOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [guideOpen, toolsOpen]);
  // 데모 영상 라이트박스 — 히어로 프레임이 좁아 글자가 안 보이므로 클릭 시 크게 재생
  const [demoOpen, setDemoOpen] = useState(false);
  useEffect(() => {
    if (!demoOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setDemoOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [demoOpen]);
  const [scIdx, setScIdx] = useState(0); // 하단 쇼케이스 활성 탭(0~3)
  useEffect(() => {
    setShown(true);
    const prevTitle = document.title;
    document.title = "loginHS — HS 품목분류 세번 조회";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  const container = { maxWidth: 1200, margin: "0 auto", padding: "0 32px" };

  const primaryBtn = {
    background: DARK,
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "14px 26px",
    fontSize: 16,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    boxShadow: "0 6px 16px -6px rgba(20,21,26,0.30)",
  };
  const ghostBtn = {
    background: "#fff",
    color: INK,
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    padding: "14px 26px",
    fontSize: 16,
    fontWeight: 600,
    cursor: "pointer",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  };

  // 하단 쇼케이스 — 탭(4컬럼) 클릭 시 바뀌는 미니 목업 4종
  const mockTitle = (t) => (
    <div
      style={{
        textAlign: "center",
        fontSize: 17,
        fontWeight: 700,
        color: INK,
        marginBottom: 18,
      }}
    >
      {t}
    </div>
  );
  const evRow = (icon, head, sub) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        border: `1px solid ${LINE}`,
        borderRadius: 10,
        padding: "11px 14px",
        background: "#fff",
      }}
    >
      {icon ? (
        <span
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: SOFT_BG,
            color: SOFT_TX,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 14,
          }}
        >
          {icon}
        </span>
      ) : null}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: INK }}>{head}</div>
        <div style={{ fontSize: 12, color: MUTED }}>{sub}</div>
      </div>
    </div>
  );
  const codeChip = (code, label, hi) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: `1px solid ${hi ? INK : LINE}`,
        borderRadius: 10,
        padding: "11px 14px",
        background: hi ? "#fafafa" : "#fff",
      }}
    >
      <span style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <code
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: INK,
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          {code}
        </code>
        <span style={{ fontSize: 13, color: BODY }}>{label}</span>
      </span>
    </div>
  );

  const renderShowcaseMockup = (idx) => {
    if (idx === 1) {
      // 근거가 보이는 분류
      return (
        <>
          {mockTitle("분류에는 근거가 따라옵니다")}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1px solid ${LINE}`,
                borderRadius: 12,
                padding: "13px 16px",
                background: BG_SOFT,
              }}
            >
              <span
                style={{ display: "flex", alignItems: "baseline", gap: 10 }}
              >
                <code
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: INK,
                    fontFamily: "JetBrains Mono, monospace",
                  }}
                >
                  4202.21-1010
                </code>
                <span style={{ fontSize: 13, color: BODY }}>
                  악어가죽 핸드백
                </span>
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: SOFT_TX,
                  background: SOFT_BG,
                  border: `1px solid ${SOFT_LINE}`,
                  borderRadius: 999,
                  padding: "3px 10px",
                }}
              >
                예시 화면
              </span>
            </div>
            {evRow("", "관세청 분류사례", "LEATHER HANDBAG → 4202.21")}
            {evRow("", "호해설 4202", "가죽제 케이스·가방 범위")}
            {evRow("", "류주 제42류", "가죽제품 분류 규정")}
          </div>
        </>
      );
    }
    if (idx === 2) {
      // 교차검증 신뢰도
      return (
        <>
          {mockTitle("LLM과 PROVE가 역할을 분리합니다")}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {evRow("", "LLM 사실추출", "가죽 · 여성용 · 핸드백")}
            {evRow("", "PROVE 검수 결정트리", "법정 갈림축 충족 → 4202.21 1010")}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                border: `1px solid ${INK}`,
                borderRadius: 12,
                padding: "13px 16px",
                background: "#fafafa",
                fontWeight: 700,
                color: INK,
                fontSize: 14,
              }}
            >
              <ShieldCheck size={16} /> 근거 사실 충족 · PROVED · 확정 가능
            </div>
          </div>
        </>
      );
    }
    if (idx === 3) {
      // 세율·FTA까지
      return (
        <>
          {mockTitle("가장 유리한 세율까지 한 번에")}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {codeChip("8%", "기본세율 (A)", false)}
            {codeChip("6.5%", "WTO 협정세율 (C)", false)}
            {codeChip("0%", "한·EU FTA 협정세율 (FEU1) — 최저", true)}
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                fontWeight: 700,
                color: INK,
                textAlign: "center",
              }}
            >
              추천: 한·EU FTA <span style={{ color: SOFT_TX }}>0%</span>{" "}
              (원산지: 이탈리아)
            </div>
          </div>
        </>
      );
    }
    // idx 0 — 한 줄로 시작 (검색 입력)
    return (
      <>
        {mockTitle("어떤 품목의 세번이 궁금하세요?")}
        <div
          style={{
            border: `1px solid ${LINE}`,
            borderRadius: 12,
            padding: "14px 14px 12px",
            background: BG_SOFT,
          }}
        >
          <div style={{ fontSize: 15, color: INK }}>
            가죽 여성용 핸드백
            <span style={{ color: SOFT_TX }}>│</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 14,
            }}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: SOFT_TX,
                  background: SOFT_BG,
                  border: `1px solid ${SOFT_LINE}`,
                  borderRadius: 7,
                  padding: "3px 9px",
                }}
              >
                PROVE
              </span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: MUTED,
                  background: "#fff",
                  border: `1px solid ${LINE}`,
                  borderRadius: 7,
                  padding: "3px 9px",
                }}
              >
                관세율표
              </span>
            </div>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: DARK,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ArrowRight size={16} />
            </span>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginTop: 14,
          }}
        >
          {evRow(
            <Layers size={16} />,
            "PROVE 근거 판정",
            "자연어 사실 → 검수 결정트리",
          )}
          {evRow(
            <Brain size={16} />,
            "보류도 설명",
            "근거 부족 시 갈림질문",
          )}
        </div>
      </>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        color: INK,
        fontFamily: "var(--font-family-sans)",
      }}
    >
      <AmendmentNoticeModal notice={latestAmendmentNotice} audience="public" />
      {/* ── Nav ── */}
      <header
        style={{
          borderBottom: `1px solid ${LINE}`,
          position: "sticky",
          top: 0,
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(8px)",
          zIndex: 20,
        }}
      >
        <div
          style={{
            ...container,
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <a
            href="/"
            aria-label="LoginHS 홈"
            onClick={(e) => {
              e.preventDefault();
              goPage("/");
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              textDecoration: "none",
            }}
          >
            <BrandMark size={26} />
            <span
              style={{
                fontFamily:
                  "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
                fontWeight: 700,
                fontSize: 19,
                letterSpacing: -0.4,
                color: INK,
              }}
            >
              LoginHS
            </span>
          </a>
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: isMobileNav ? 12 : 24,
              fontSize: 14,
              color: MUTED,
            }}
          >
            {!isMobileNav && (
              <>
                <a
                  href="/#features"
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  기능
                </a>
                <a
                  href="/#prove"
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  PROVE 엔진
                </a>
                <a
                  href="/#data"
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  데이터
                </a>
                <a
                  href="/?page=pricing"
                  onClick={(e) => {
                    e.preventDefault();
                    window.history.pushState({}, "", "/?page=pricing");
                    window.dispatchEvent(new PopStateEvent("popstate"));
                  }}
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  요금제
                </a>
                <div ref={guideMenuRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setToolsOpen(false);
                      setGuideOpen((v) => !v);
                    }}
                    aria-haspopup="true"
                    aria-expanded={guideOpen}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: MUTED,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    가이드
                    <span style={{ fontSize: 10, lineHeight: 1 }}>▾</span>
                  </button>
                  {guideOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        paddingTop: 8,
                        zIndex: 60,
                      }}
                    >
                      <div
                        style={{
                          minWidth: 154,
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                          padding: 6,
                        }}
                      >
                        {NAV_GUIDES.map(([label, href]) => (
                          <a
                            key={href}
                            href={href}
                            onClick={(e) => {
                              e.preventDefault();
                              setGuideOpen(false);
                              goPage(href);
                            }}
                            style={{
                              display: "block",
                              padding: "8px 12px",
                              borderRadius: 7,
                              color: BODY,
                              textDecoration: "none",
                              fontSize: 13.5,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div ref={toolsMenuRef} style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setGuideOpen(false);
                      setToolsOpen((v) => !v);
                    }}
                    aria-haspopup="true"
                    aria-expanded={toolsOpen}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: MUTED,
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    무료 도구
                    <span style={{ fontSize: 10, lineHeight: 1 }}>▾</span>
                  </button>
                  {toolsOpen && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        paddingTop: 8,
                        zIndex: 60,
                      }}
                    >
                      <div
                        style={{
                          minWidth: 172,
                          background: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
                          padding: 6,
                        }}
                      >
                        <div
                          style={{
                            padding: "6px 12px 5px",
                            color: "#9ca3af",
                            fontSize: 11.5,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          로그인 없이 바로 사용
                        </div>
                        {NAV_TOOLS.map(([label, href]) => (
                          <a
                            key={href}
                            href={href}
                            onClick={(e) => {
                              e.preventDefault();
                              setToolsOpen(false);
                              goPage(href);
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#f3f4f6";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                            style={{
                              display: "block",
                              padding: "8px 12px",
                              borderRadius: 7,
                              color: BODY,
                              textDecoration: "none",
                              fontSize: 13.5,
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <a
                  href="/?page=news"
                  onClick={(e) => {
                    e.preventDefault();
                    goPage("/?page=news");
                  }}
                  style={{
                    color: MUTED,
                    textDecoration: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  새소식
                </a>
              </>
            )}
            <a
              href="/?page=install"
              onClick={(e) => {
                e.preventDefault();
                goPage("/?page=install");
              }}
              title="휴대폰 홈 화면에 앱으로 설치"
              data-testid="landing-install"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                color: INK,
                textDecoration: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                border: "1px solid #e3e5ea",
                borderRadius: 9,
                padding: "7px 12px",
                fontSize: 14,
              }}
            >
              <Smartphone size={15} /> 앱 설치
            </a>
            <button
              onClick={onLoginClick}
              style={{
                background: DARK,
                color: "#fff",
                border: "none",
                borderRadius: 9,
                padding: "8px 16px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              로그인 / 시작하기
            </button>
          </nav>
        </div>
      </header>

      {/* ── Hero (좌 텍스트 / 우 제품 스크린샷) ── */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
          background: `radial-gradient(1100px 460px at 80% -10%, ${TINT_DEEP} 0%, rgba(236,239,251,0) 60%)`,
        }}
      >
        <div
          style={{
            ...container,
            maxWidth: 1360,
            paddingTop: 112,
            paddingBottom: 96,
            display: "flex",
            flexWrap: "wrap",
            gap: 64,
            alignItems: "center",
            opacity: shown ? 1 : 0,
            transform: shown ? "none" : "translateY(12px)",
            transition: "all 0.5s ease",
          }}
        >
          {/* 좌 — 카피 (이미지보다 좁게) */}
          <div style={{ flex: "1 1 360px", maxWidth: 500 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                color: SOFT_TX,
                background: SOFT_BG,
                border: `1px solid ${SOFT_LINE}`,
                borderRadius: 999,
                padding: "5px 14px",
                fontWeight: 600,
              }}
            >
              <ShieldCheck size={14} /> 관세청 공개 품목분류 자료 기반
            </span>
            <h1
              style={{
                fontSize: 54,
                lineHeight: 1.15,
                fontWeight: 800,
                margin: "24px 0 0",
                letterSpacing: -1.4,
                color: INK,
              }}
            >
              세번 후보는 빠르게,
              <br />
              근거는 분명하게.
            </h1>
            <p
              style={{
                fontSize: 18,
                color: MUTED,
                margin: "20px 0 0",
                maxWidth: 520,
                lineHeight: 1.65,
              }}
            >
              품명을 넣으면 10자리 세번 후보와 분류사례·법령·세율 근거를 함께
              보여줍니다. 판단에 필요한 사실이 부족하면 갈림 질문으로 확인합니다.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 32,
                flexWrap: "wrap",
              }}
            >
              <button onClick={onLoginClick} style={primaryBtn}>
                무료로 시작하기 <ArrowRight size={18} />
              </button>
              <a href="/#features" style={ghostBtn}>
                <Search size={18} /> 기능 살펴보기
              </a>
              <a
                href="/?page=install"
                onClick={(e) => {
                  e.preventDefault();
                  goPage("/?page=install");
                }}
                style={ghostBtn}
              >
                <Smartphone size={18} /> 모바일 앱 설치
              </a>
            </div>
          </div>

          {/* 우 — 제품 스크린샷 (텍스트보다 넓게, Ponder 식 큰 이미지) */}
          <div
            style={{ position: "relative", flex: "1.7 1 560px", minWidth: 0 }}
          >
            {/* 데코 곡선 (아주 미세) */}
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 600 460"
              fill="none"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 0,
                pointerEvents: "none",
              }}
              preserveAspectRatio="none"
            >
              <path
                d="M40 360 C 180 460, 430 420, 560 250"
                stroke={SOFT_LINE}
                strokeWidth="1.5"
                fill="none"
              />
            </svg>
            <div
              style={{
                position: "relative",
                zIndex: 1,
                borderRadius: 18,
                padding: 14,
                background: `linear-gradient(150deg, ${TINT_DEEP} 0%, ${TINT} 100%)`,
                border: `1px solid ${SOFT_LINE}`,
              }}
            >
              <div
                style={{
                  position: "relative",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "#fff",
                  border: `1px solid ${LINE}`,
                  boxShadow: "0 22px 50px -22px rgba(20,21,26,0.32)",
                }}
              >
                {heroImgOk && (
                  <button
                    type="button"
                    onClick={() => setDemoOpen(true)}
                    style={{
                      position: "absolute",
                      right: 10,
                      bottom: 10,
                      zIndex: 2,
                      background: "rgba(22,21,26,0.82)",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "7px 12px",
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    클릭하여 크게 보기
                  </button>
                )}
                {heroImgOk ? (
                  // 데모 영상(무음·자동반복, 471KB mp4) — 방호복 4015→갈림 질문→6210 확정 플로우.
                  // poster=정적 캡처(로딩·미지원 시), 영상 로드 실패 시 img 폴백과 동일 처리.
                  <video
                    src={DEMO_MP4}
                    poster={HERO_SHOT}
                    autoPlay
                    loop
                    muted
                    playsInline
                    onClick={() => setDemoOpen(true)}
                    onError={() => setHeroImgOk(false)}
                    aria-label="LoginHS 세번 분류 데모 — 이전 화면의 검색·질문 흐름 시연. 최종 세번은 현재 상품 사실과 규칙으로 재확인. 클릭하면 크게 재생"
                    style={{ width: "100%", height: "auto", display: "block", cursor: "zoom-in" }}
                  />
                ) : (
                  // 스크린샷 미배치 시 fallback — 깨진 이미지 대신 안내 목업
                  <div
                    style={{
                      minHeight: 360,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      color: MUTED,
                      background: BG_SOFT,
                      fontSize: 14,
                      textAlign: "center",
                      padding: 24,
                    }}
                  >
                    <Search size={28} color={SOFT_TX} />
                    <div style={{ fontWeight: 700, color: INK }}>
                      세번 분류 화면 미리보기
                    </div>
                    <div style={{ fontSize: 13 }}>
                      상품 사실 확인부터 판정 근거 확인까지 이어집니다.
                    </div>
                  </div>
                )}
                <div style={{ padding: "8px 12px", fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
                  이전 화면으로 제작한 사용 흐름 시연입니다. 영상의 세번은 현재 상품 사실과 적용 규칙으로 다시 확인해야 합니다.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 데모 라이트박스 — 히어로 영상 클릭 시 대형 재생 (배경/ESC/닫기로 종료) ── */}
      {demoOpen && (
        <div
          onClick={() => setDemoOpen(false)}
          role="dialog"
          aria-label="세번 분류 데모 크게 보기"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(10,10,14,0.82)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <button
            type="button"
            onClick={() => setDemoOpen(false)}
            aria-label="닫기"
            style={{
              position: "absolute",
              top: 18,
              right: 22,
              background: "transparent",
              color: "#fff",
              border: "none",
              fontSize: 30,
              lineHeight: 1,
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            ×
          </button>
          <video
            src={DEMO_MP4}
            autoPlay
            loop
            muted
            controls
            playsInline
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "94vw",
              maxHeight: "90vh",
              borderRadius: 12,
              boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
              background: "#000",
            }}
          />
        </div>
      )}

      {/* ── Stats (데이터 권위 + 규모를 한 곳에 — 출처를 각 지표에 부착) ── */}
      <section
        id="data"
        style={{ ...container, paddingTop: 4, paddingBottom: 8 }}
      >
        <div
          style={{
            textAlign: "center",
            fontSize: 13,
            color: MUTED,
            marginBottom: 22,
            letterSpacing: 0.2,
          }}
        >
          관세청 권위 데이터와 국제 표준(WCO HS)을 근거로 분류합니다
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 16,
          }}
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              style={{
                border: `1px solid ${LINE}`,
                borderRadius: 14,
                padding: "24px 16px",
                background: "#fff",
                boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 28, fontWeight: 800, color: INK }}>
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: INK,
                  marginTop: 6,
                }}
              >
                {s.label}
              </div>
              <div style={{ fontSize: 12, color: "#9aa0ad", marginTop: 4 }}>
                {s.source}
              </div>
            </div>
          ))}
        </div>

        {/* 해외 분류사례 — 국가별 규모 (2025~2026 최신까지) */}
        <div style={{ marginTop: 40 }}>
          <div
            style={{
              textAlign: "center",
              fontSize: 13,
              fontWeight: 700,
              color: SOFT_TX,
              letterSpacing: 1,
              marginBottom: 6,
            }}
          >
            WORLDWIDE PRECEDENTS
          </div>
          <h3
            style={{
              textAlign: "center",
              fontSize: 22,
              fontWeight: 800,
              color: INK,
              margin: "0 0 6px",
              letterSpacing: -0.4,
            }}
          >
            국내뿐 아니라, 미국·EU·일본의 결정까지
          </h3>
          <p
            style={{
              textAlign: "center",
              fontSize: 13.5,
              color: BODY,
              maxWidth: 600,
              margin: "0 auto 22px",
              lineHeight: 1.65,
            }}
          >
            같은 물품이 해외에서 어떻게 분류됐는지 — 미국 CBP·EU BTI·일본 등
            14개국 관세당국의 공식 결정 51만 건을 2025~2026년 최신까지
            수록했습니다.
          </p>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 10,
            }}
          >
            {[
              ["🇪🇺", "EU", "315,000"],
              ["🇯🇵", "일본", "52,000"],
              ["🇺🇸", "미국", "33,000"],
              ["🇹🇼", "대만", "5,700"],
              ["🇹🇭", "태국", "5,400"],
              ["🇵🇭", "필리핀", "3,800"],
              ["🇬🇧", "영국", "3,000"],
              ["", "+ 호주·스위스·칠레·캐나다 등", ""],
            ].map(([flag, name, n]) => (
              <div
                key={name}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: `1px solid ${LINE}`,
                  borderRadius: 999,
                  padding: "8px 15px",
                  background: "#fff",
                  fontSize: 13.5,
                }}
              >
                <span style={{ fontSize: 15 }}>{flag}</span>
                <span style={{ fontWeight: 700, color: INK }}>{name}</span>
                {n && (
                  <span
                    style={{
                      fontFamily: "JetBrains Mono, monospace",
                      fontSize: 12.5,
                      color: MUTED,
                    }}
                  >
                    {n}건
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section
        id="features"
        style={{ background: TINT, padding: "84px 0", marginTop: 64 }}
      >
        <div style={container}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: SOFT_TX,
                letterSpacing: 1,
              }}
            >
              CORE FEATURES
            </div>
            <h2
              style={{
                fontSize: 32,
                fontWeight: 800,
                margin: "10px 0 0",
                letterSpacing: -0.5,
                color: INK,
              }}
            >
              세번 하나를 위한 모든 것
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: 20,
            }}
          >
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  style={{
                    background: "#fff",
                    border: `1px solid ${LINE}`,
                    borderRadius: 16,
                    padding: 24,
                    boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      background: SOFT_BG,
                      color: SOFT_TX,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 16 }}>
                    {f.title}
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                    {f.en}
                  </div>
                  <p
                    style={{
                      fontSize: 14,
                      color: BODY,
                      lineHeight: 1.6,
                      marginTop: 12,
                    }}
                  >
                    {f.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── 신뢰할 수 있는 데이터 (공식 원문 기반 — 출처 투명 공개) ── */}
      <section style={{ ...container, paddingTop: 96 }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: SOFT_TX,
              letterSpacing: 1,
            }}
          >
            TRUSTED DATA
          </div>
          <h2
            style={{
              fontSize: 32,
              fontWeight: 800,
              margin: "10px 0 0",
              letterSpacing: -0.5,
              color: INK,
            }}
          >
            공식 원문에 근거한 데이터
          </h2>
          <p
            style={{
              fontSize: 15,
              color: BODY,
              lineHeight: 1.65,
              margin: "14px auto 0",
              maxWidth: 660,
            }}
          >
            loginHS의 세번 정보는 관세청·국가법령정보 원문에서 옵니다. 어디서
            왔는지, 얼마나 자주 갱신되는지 투명하게 공개합니다.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 20,
          }}
        >
          {TRUST_ITEMS.map((t) => (
            <div
              key={t.title}
              style={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 16,
                padding: 24,
                boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  fontSize: 12,
                  fontWeight: 700,
                  color: SOFT_TX,
                  background: SOFT_BG,
                  borderRadius: 8,
                  padding: "4px 10px",
                }}
              >
                {t.tag}
              </span>
              <div
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  marginTop: 14,
                  color: INK,
                }}
              >
                {t.title}
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: BODY,
                  lineHeight: 1.6,
                  marginTop: 8,
                }}
              >
                {t.desc}
              </p>
            </div>
          ))}
        </div>
        <p
          style={{
            fontSize: 13,
            color: MUTED,
            lineHeight: 1.6,
            textAlign: "center",
            maxWidth: 720,
            margin: "28px auto 0",
          }}
        >
          ※ 관세율·요건·원산지 등은 관세청 원문 기반 스냅샷으로 수시 갱신되며,
          관세제도 안내·AI 가이드는 참고용 요약입니다. 신고 전 원문 확인을
          권장합니다.
        </p>
      </section>

      {/* ── PROVE Framework ── */}
      <section id="prove" style={{ ...container, paddingTop: 96 }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: SOFT_TX,
              letterSpacing: 1,
            }}
          >
            THE PROVE FRAMEWORK
          </div>
          <h2
            style={{
              fontSize: 32,
              fontWeight: 800,
              margin: "10px 0 0",
              letterSpacing: -0.5,
              color: INK,
            }}
          >
            추측하지 않습니다. 증명합니다.
          </h2>
          <p
            style={{
              fontSize: 15,
              color: BODY,
              maxWidth: 760,
              margin: "14px auto 0",
              lineHeight: 1.7,
            }}
          >
            자연어로 검색하면 <strong>PROVE</strong>가 문장을 분류 사실로 해체하고,
            검수된 류에서는 결정트리가 세번을 판정합니다. AI는 사실을 제안할 뿐 —
            근거가 부족하거나 검수 범위 밖이면 확정하지 않고 필요한 질문을 남깁니다.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
          }}
        >
          {[
            {
              initial: "P",
              en: "Parse",
              title: "자연어를 사실로 해체합니다",
              desc: "LLM이 품명과 문서에서 재질·구성·용도·사용처·형태·작동방식을 구조화합니다. 수치와 법률 판단은 맡기지 않습니다.",
            },
            {
              initial: "R",
              en: "Rule",
              title: "검수 규칙으로 판정합니다",
              desc: "통칙·부주·류주·호와 소호 문언, 수치 경계, 형제 배제와 잔여조건을 결정론적으로 적용합니다. 기존 HSK는 판정 입력이 아닙니다.",
            },
            {
              initial: "O",
              en: "Operate",
              title: "근거가 충분할 때만 진행합니다",
              desc: "문서 명시 사실과 사용자 확인 사실을 우선하고, 충돌·미확정·검수 범위 밖에서는 세번을 만들지 않습니다. 부족한 갈림사실만 다시 묻습니다.",
            },
          ].map((m) => (
            <div
              key={m.initial}
              style={{
                background: "#fff",
                border: `1px solid ${LINE}`,
                borderRadius: 16,
                padding: 24,
                boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: SOFT_BG,
                    color: SOFT_TX,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    fontSize: 18,
                    fontWeight: 800,
                  }}
                >
                  {m.initial}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: SOFT_TX,
                      letterSpacing: 1,
                      textTransform: "uppercase",
                    }}
                  >
                    {m.en}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
                    {m.title}
                  </div>
                </div>
              </div>
              <p
                style={{
                  fontSize: 14,
                  color: BODY,
                  lineHeight: 1.7,
                  margin: "16px 0 0",
                }}
              >
                {m.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Verdict — 결정 구조 그래프를 판정 단계의 실물 증거로 배치 */}
        <div
          style={{
            marginTop: 20,
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: 16,
            padding: 24,
            boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
              alignItems: "start",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: SOFT_BG,
                  color: SOFT_TX,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: 18,
                  fontWeight: 800,
                }}
              >
                V
              </div>
              <div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: SOFT_TX,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}
                >
                  Verdict
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
                  판정 로직이 세번을 결정합니다
                </div>
              </div>
            </div>
            <p
              style={{
                fontSize: 14,
                color: BODY,
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              검수 결정트리가 문서 명시 사실과 사용자 확인 사실로 단일 경로를
              지지할 때만 10자리 세번을 확정합니다. 사실이 부족하거나 충돌하면
              추측하지 않고 갈림질문을 표시하며, 검수 범위 밖은 후보로만 남깁니다.
            </p>
          </div>
          <div
            style={{
              marginTop: 24,
              border: `1px solid ${LINE}`,
              borderRadius: 14,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div style={{ padding: "14px 18px", fontSize: 13, lineHeight: 1.7 }}>
              <strong>설명용 예시 · 확인된 범위까지만 표시합니다</strong><br />
              면 100% · 남성용 · 편물 티셔츠 → 국제 6단위 후보 6109.10.
              국내 10자리 세번은 활성 관세율표와 필요한 세부 조건을 확인한 뒤 판정합니다.
            </div>
            <ProveDecisionGraph
              query="100% 면 남성용 편물 티셔츠"
              result={{
                classification_tree_output_field: "apparel_tree_shadow",
                classification_tree: {
                  action: "supported_hs6", supported_chapter: "61", supported_hs4: "6109", supported_hs6: "610910",
                  reason: "official_leaf_lookup_unavailable",
                },
                comparison: { tree_may_override_legacy: false },
                confirmed_facts: {},
                prove: { status: "needs_facts", proof: { confirmed_fact_count: 0 } },
              }}
            />
          </div>
        </div>

        <div
          style={{
            marginTop: 20,
            background: "#fff",
            border: `1px solid ${LINE}`,
            borderRadius: 16,
            padding: 24,
            boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: SOFT_BG,
                color: SOFT_TX,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              E
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: SOFT_TX,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                }}
              >
                Evidence
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, marginTop: 2 }}>
                근거가 남습니다
              </div>
            </div>
          </div>
          <p
            style={{
              fontSize: 14,
              color: BODY,
              lineHeight: 1.7,
              margin: 0,
            }}
          >
            어떤 후보가 어떤 분류 기준에 따라 채택되고 배제됐는지,
            관세율표·통칙·주·분류사례와 함께 건별로 기록됩니다. 신고이력까지
            연결해 고객에게는 확인 가능한 판정 근거로, 관세청 심사 대응에는
            소명자료 작성의 근거로 활용됩니다.
          </p>
        </div>

        {/* PROVE의 약속 — 보류와 확정 모두 설명 가능한 분류 */}
        <div
          style={{
            marginTop: 28,
            border: `1px solid ${SOFT_LINE}`,
            background: BG_SOFT,
            borderRadius: 16,
            padding: "22px 28px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>
            확신이 없으면, 확정하지 않습니다. 확정하면, 증명할 수 있습니다.
          </div>
          <p
            style={{
              fontSize: 14,
              color: BODY,
              lineHeight: 1.7,
              maxWidth: 720,
              margin: "8px auto 0",
            }}
          >
            품목분류는 같은 물품도 재질·용도에 따라 세번이 갈리는 일입니다.
            loginHS는 증거가 일치할 때만 확정하고, 모호하면 모호하다고 말합니다.
            그리고 확정한 모든 세번에는 — 왜 그 세번인지 보여줄 근거가 붙습니다.
            PROVE는 그 약속의 이름입니다.
          </p>
        </div>
      </section>

      {/* ── 변경고시 위험 추적 — 공식 원문과 실제 신고 위험을 한 화면에 ── */}
      <section id="amendment-risk" style={{ ...container, paddingTop: 96 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 48,
            alignItems: "center",
          }}
        >
          <div style={{ maxWidth: 480 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: SOFT_TX,
                letterSpacing: 1,
              }}
            >
              AMENDMENT RISK TRACKING
            </div>
            <h2
              style={{
                fontSize: "clamp(28px, 3vw, 38px)",
                lineHeight: 1.25,
                fontWeight: 800,
                margin: "12px 0 0",
                letterSpacing: -0.7,
                color: INK,
              }}
            >
              현재 세번만 보면,
              <br />
              놓치는 위험이 있습니다
            </h2>
            <p
              style={{
                fontSize: 15,
                color: BODY,
                lineHeight: 1.75,
                margin: "18px 0 0",
              }}
            >
              LoginHS는 관세청 관세율표에 국가법령정보센터 변경고시를
              연결합니다. 변경 전·후 세번, 시행일, 변경 사유와 원문 쪽수를
              한 화면에서 확인하고, 변경 전 세번으로 신고가 계속되면 별도로
              경고합니다.
            </p>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10,
                marginTop: 24,
              }}
            >
              {[
                "변경 전·후 세번 연결",
                "변경 사유 함께 표시",
                "공식 PDF 정확 쪽수",
                "최근 옛 세번 신고 경고",
              ].map((label) => (
                <div
                  key={label}
                  style={{
                    border: `1px solid ${LINE}`,
                    borderRadius: 10,
                    padding: "10px 12px",
                    background: BG_SOFT,
                    fontSize: 13,
                    fontWeight: 650,
                    color: INK,
                  }}
                >
                  <span style={{ color: "#2563eb", marginRight: 7 }}>✓</span>
                  {label}
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={onLoginClick}
              style={{ ...primaryBtn, marginTop: 26 }}
            >
              실제 화면에서 확인 <ArrowRight size={16} />
            </button>
          </div>

          <div
            aria-label="품목분류 변경고시 위험 추적 샘플"
            style={{
              border: `1px solid ${LINE}`,
              borderRadius: 18,
              padding: "clamp(16px, 2vw, 24px)",
              background: "#fff",
              boxShadow: "0 18px 48px rgba(22,21,26,0.10)",
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', Consolas, monospace",
                    fontSize: 21,
                    fontWeight: 800,
                    color: INK,
                  }}
                >
                  8501.10-1000
                </div>
                <div style={{ fontSize: 11.5, color: MUTED, marginTop: 5 }}>
                  중량단위 <strong style={{ color: INK }}>KG</strong>
                  <span style={{ margin: "0 8px", color: LINE }}>|</span>
                  수량단위 <strong style={{ color: INK }}>U</strong>
                </div>
              </div>
              <a
                href={MOTOR_PRECEDENT_URL}
                target="_blank"
                rel="noreferrer"
                title="2020-02-10 관세청 품목분류사례 원문 새 창에서 열기"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  border: "1px solid #cbd5e1",
                  borderRadius: 7,
                  padding: "7px 9px",
                  color: "#2563eb",
                  textDecoration: "none",
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                관세청 품목분류사례 원문 <ExternalLink size={13} />
              </a>
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 12 }}>
              종전 품목분류사례 <strong style={{ color: INK }}>시행일 2020-02-10</strong>
              <span style={{ margin: "0 7px", color: LINE }}>|</span>
              관세청 CLIP 참조번호 품목분류3과-11785
            </div>

            <div
              style={{
                marginTop: 16,
                border: "1px solid #d8dee8",
                borderLeft: "4px solid #64748b",
                background: "#fbfcfd",
                borderRadius: 11,
                padding: "16px 16px 14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  color: INK,
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                <TriangleAlert size={15} color="#b45309" /> 품목분류 변경고시 이력
              </div>
              <p
                style={{
                  fontSize: 12.5,
                  lineHeight: 1.6,
                  color: BODY,
                  margin: "9px 0 0",
                }}
              >
                이 세번의 일부 특정 품목이 변경고시로 다른 세번으로
                변경되었습니다.
                <strong style={{ color: "#9a5b13" }}>
                  {" "}최근 4개 분기, 종전 세번 관련 품명 신고 358건 감지
                </strong>
              </p>

              <div
                aria-label="358건 산출 근거"
                style={{
                  marginTop: 12,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  borderRadius: 9,
                  padding: "11px 12px",
                }}
              >
                <div
                  style={{
                    color: INK,
                    fontSize: 12.5,
                    fontWeight: 800,
                    marginBottom: 7,
                  }}
                >
                  358건 산출 근거
                </div>
                <dl
                  style={{
                    display: "grid",
                    gridTemplateColumns: "88px minmax(0, 1fr)",
                    columnGap: 10,
                    rowGap: 5,
                    margin: 0,
                    fontSize: 11.5,
                    lineHeight: 1.55,
                  }}
                >
                  <dt style={{ color: MUTED }}>데이터 원천</dt>
                  <dd style={{ margin: 0, color: BODY }}>
                    LoginHS 신고이력 데이터(세번·신고일·신고품명)
                  </dd>
                  <dt style={{ color: MUTED }}>집계 대상</dt>
                  <dd style={{ margin: 0, color: BODY }}>
                    종전 세번 8501.10-1000 중 변경고시 품명에서 추출한
                    MOTOR·NTWC 키워드 적중 행
                  </dd>
                  <dt style={{ color: MUTED }}>기간·산식</dt>
                  <dd style={{ margin: 0, color: BODY }}>
                    신고이력 최신 분기를 포함한 최근 4개 분기의 적중 행 수
                  </dd>
                  <dt style={{ color: MUTED }}>실측 결과</dt>
                  <dd style={{ margin: 0, color: BODY }}>
                    전체 보유기간 4,511행 중 최근 4개 분기 358행
                    <span style={{ color: MUTED }}> · 2026-08-13 운영 API 확인</span>
                  </dd>
                </dl>
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: "1px solid #f1f5f9",
                    color: MUTED,
                    fontSize: 11.5,
                    lineHeight: 1.5,
                  }}
                >
                  확정 오분류 건수가 아니라, 변경고시 대상과 유사한 신고가
                  종전 세번에서 지속되는지 선제 점검하는 위험 신호입니다.
                </div>
              </div>

              <div
                style={{
                  marginTop: 13,
                  paddingTop: 13,
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 7,
                    fontSize: 12.5,
                  }}
                >
                  <span aria-hidden="true">→</span>
                  <strong
                    style={{
                      fontFamily: "'JetBrains Mono', Consolas, monospace",
                      color: INK,
                    }}
                  >
                    8413.70-9090
                  </strong>
                  <span style={{ color: MUTED }}>
                    변경고시 2022-21 · 고시일 2022-05-26
                  </span>
                  <button
                    type="button"
                    onClick={() => setAmendmentPdf(MOTOR_AMENDMENT_EVIDENCE)}
                    title="변경고시 2022-21 공식 PDF 323쪽을 LoginHS에서 보기"
                    aria-label="변경고시 원문 323쪽 보기"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      color: "#2563eb",
                      fontWeight: 700,
                      font: "inherit",
                      background: "none",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <FileText size={13} /> 변경고시 원문 323쪽
                  </button>
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: INK,
                    fontWeight: 650,
                    marginTop: 8,
                  }}
                >
                  MOTOR; NTWC021S02
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: BODY,
                    lineHeight: 1.55,
                    marginTop: 5,
                  }}
                >
                  전동기에 의해 임펠러가 고속 회전해 원심력으로 물을
                  배수하므로 제8413.70-9090호로 변경
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 8,
                  marginTop: 12,
                }}
              >
                {["8529.90-9500", "8543.90-9090"].map((code) => (
                  <div
                    key={code}
                    style={{
                      border: "1px solid #e2e8f0",
                      background: "#fff",
                      borderRadius: 8,
                      padding: "9px 10px",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono', Consolas, monospace",
                        fontSize: 12,
                        fontWeight: 750,
                        color: INK,
                      }}
                    >
                      {code} → 8501.10-1000
                    </div>
                    <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>
                      고시 2022-14 · Auto Focus Actuator
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <p
              style={{
                fontSize: 11.5,
                lineHeight: 1.55,
                color: MUTED,
                margin: "12px 2px 0",
              }}
            >
              실제 운영 화면을 축약한 샘플입니다. 변경고시는 특정 품목에
              적용되며, 해당 세번 전체가 일괄 폐지되었다는 의미가 아닙니다.
            </p>
          </div>
        </div>
      </section>

      {/* ── Proven (실측 예시 — 운영 환경 실제 결과) ── */}
      <section
        id="proven"
        style={{ background: TINT, padding: "84px 0", marginTop: 96 }}
      >
        <div style={container}>
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: SOFT_TX,
                letterSpacing: 1,
              }}
            >
              PROVEN IN ACTION
            </div>
            <h2
              style={{
                fontSize: 32,
                fontWeight: 800,
                margin: "10px 0 0",
                letterSpacing: -0.5,
                color: INK,
              }}
            >
              실제로 이렇게 작동합니다
            </h2>
            <p style={{ fontSize: 14, color: MUTED, margin: "12px 0 0" }}>
              운영 환경 실측 결과 그대로입니다 — 로그인하면 같은 키워드로 직접
              확인할 수 있습니다.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 16,
              marginTop: 34,
            }}
          >
            {[
              {
                kw: "립스틱",
                result: "3304.10-1000 즉시 확정",
                note: "신뢰도 high — 사례·해설·세율 근거 동봉. 립스틱 '파우치'(가방)에 속지 않습니다.",
              },
              {
                kw: "자전거",
                result: "증거 후보 카드 제시",
                note: "완구용(9503) vs 일반(8712) — 증거가 갈리면 후보별 사례·유사도를 펼쳐 1클릭 확정.",
              },
              {
                kw: "선인장",
                result: "분류사례 38건 즉시",
                note: "국내 22건 + 미국·EU 등 해외 16건. 결정코드별로 좁혀가며 비교.",
              },
              {
                kw: "가죽핸드백",
                result: "자동 분절 → 42류 정확 진입",
                note: "붙여 써도 '가죽+핸드백'으로 이해합니다 — 검색이 아니라 의미로.",
              },
            ].map((d) => (
              <div
                key={d.kw}
                style={{
                  background: "#fff",
                  border: `1px solid ${LINE}`,
                  borderRadius: 14,
                  padding: "20px 22px",
                  boxShadow: "0 1px 2px rgba(20,21,26,0.04)",
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    color: MUTED,
                    fontFamily: "JetBrains Mono, monospace",
                  }}
                >
                  검색 ▸ “{d.kw}”
                </div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 800,
                    color: INK,
                    marginTop: 8,
                  }}
                >
                  → {d.result}
                </div>
                <p
                  style={{
                    fontSize: 13,
                    color: BODY,
                    lineHeight: 1.6,
                    marginTop: 8,
                    marginBottom: 0,
                  }}
                >
                  {d.note}
                </p>
              </div>
            ))}
          </div>
          {/* 측정 지표 스트립 */}
          <div
            style={{
              marginTop: 26,
              border: `1px solid ${SOFT_LINE}`,
              background: "#fff",
              borderRadius: 14,
              padding: "18px 24px",
              display: "flex",
              flexWrap: "wrap",
              gap: 28,
              justifyContent: "center",
              alignItems: "baseline",
            }}
          >
            {[
              ["사실 확인", "부족한 상품 정보를 질문"],
              ["규칙 판정", "확인된 사실로 경로 계산"],
              ["근거 확인", "판정에 사용한 사실과 자료"],
            ].map(([v, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: INK }}>
                  {v}
                </span>
                <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>
                  {l}
                </span>
              </div>
            ))}
            <div style={{ flexBasis: "100%", textAlign: "center" }}>
              <span style={{ fontSize: 11, color: MUTED }}>
                후보 제안과 판정 완료를 구분합니다. 정보가 부족하거나 조건이 충돌하면 추가 확인을 요청합니다.
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Showcase (Ponder 식 — 옅은 패널 + 제품 목업 + 4컬럼) ── */}
      <section style={{ ...container, paddingTop: 96, paddingBottom: 24 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2
            style={{
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: -0.6,
              color: INK,
              margin: 0,
            }}
          >
            빠르게가 아니라, 정확하게.
          </h2>
          <p
            style={{
              fontSize: 17,
              color: MUTED,
              margin: "12px auto 0",
              maxWidth: 640,
              lineHeight: 1.6,
            }}
          >
            LoginHS 는 분류 결과만 던지지 않습니다. 그 분류가 왜 맞는지
            사례·법령·세율 근거를 함께 보여주는, 관세 실무를 위한 분류
            도구입니다.
          </p>
        </div>

        {/* 옅은 패널 + 떠 있는 검색 목업 카드 */}
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 24,
            background: `linear-gradient(180deg, ${TINT_DEEP} 0%, ${TINT} 100%)`,
            border: `1px solid ${SOFT_LINE}`,
            padding: "56px 24px",
          }}
        >
          {/* 데코 곡선 (아주 미세) */}
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 1100 520"
            fill="none"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 0,
              pointerEvents: "none",
            }}
          >
            <path
              d="M-20 120 C 240 -40, 360 320, 760 180 S 1180 60, 1180 60"
              stroke="#ffffff"
              strokeOpacity="0.7"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M-20 420 C 300 520, 520 300, 1120 460"
              stroke="#ffffff"
              strokeOpacity="0.5"
              strokeWidth="2"
              fill="none"
            />
          </svg>

          {/* 검색 목업 카드 */}
          <div
            style={{
              position: "relative",
              zIndex: 1,
              maxWidth: 640,
              margin: "0 auto",
              background: "#fff",
              borderRadius: 16,
              border: `1px solid ${LINE}`,
              boxShadow: "0 24px 60px -28px rgba(20,21,26,0.28)",
              padding: "28px 24px 24px",
              minHeight: 252,
            }}
          >
            {renderShowcaseMockup(scIdx)}
          </div>
        </div>

        {/* 4컬럼 서브기능 (윗줄 구분선) */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 28,
            marginTop: 44,
          }}
        >
          {SHOWCASE_COLS.map((c, i) => {
            const active = i === scIdx;
            return (
              <button
                key={c.title}
                type="button"
                onClick={() => setScIdx(i)}
                style={{
                  textAlign: "left",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  borderTop: `2px solid ${active ? INK : LINE}`,
                  paddingTop: 16,
                  transition: "border-color 0.15s",
                }}
              >
                <div
                  style={{
                    fontSize: 17,
                    fontWeight: 700,
                    color: active ? INK : "#a2a6b3",
                    transition: "color 0.15s",
                  }}
                >
                  {c.title}
                </div>
                <p
                  style={{
                    fontSize: 14,
                    color: active ? BODY : MUTED,
                    lineHeight: 1.6,
                    marginTop: 10,
                  }}
                >
                  {c.desc}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── 세그먼트 3분기 — 관세사 / 수출입기업 / 포워더·특송 ── */}
      <section style={{ ...container, paddingTop: 96 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <h2
            style={{
              fontSize: 30,
              fontWeight: 800,
              letterSpacing: -0.6,
              color: INK,
              margin: 0,
            }}
          >
            일하는 방식에 맞게 쓰세요
          </h2>
          <p
            style={{
              fontSize: 17,
              color: MUTED,
              margin: "12px auto 0",
              maxWidth: 640,
              lineHeight: 1.6,
            }}
          >
            같은 엔진, 다른 쓰임새 — 관세사무소의 검토 도구로, 수출입기업의
            확인 도구로, 특송·포워더의 대량 처리 API로.
          </p>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 20,
          }}
        >
          {SEGMENTS.map((s) => {
            const Icon = s.icon;
            const go = (e) => {
              e.preventDefault();
              if (s.cta.action === "cases") {
                // 서버렌더 공개 페이지 — SPA 라우팅이 아니라 전체 네비게이션
                window.location.href = "/cases";
                return;
              }
              window.history.pushState({}, "", "/?page=" + s.cta.action);
              window.dispatchEvent(new PopStateEvent("popstate"));
            };
            return (
              <div
                key={s.badge}
                style={{
                  background: BG_SOFT,
                  border: `1px solid ${SOFT_LINE}`,
                  borderRadius: 16,
                  padding: "26px 24px 24px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 14,
                  }}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      background: SOFT_BG,
                      color: SOFT_TX,
                      border: `1px solid ${SOFT_LINE}`,
                      borderRadius: 999,
                      padding: "4px 12px",
                      fontSize: 12.5,
                      fontWeight: 700,
                    }}
                  >
                    <Icon size={13} /> {s.badge}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 19,
                    fontWeight: 800,
                    letterSpacing: -0.3,
                    color: INK,
                  }}
                >
                  {s.title}
                </div>
                <p
                  style={{
                    fontSize: 14,
                    color: BODY,
                    lineHeight: 1.7,
                    marginTop: 10,
                    flexGrow: 0,
                  }}
                >
                  {s.desc}
                </p>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: "6px 0 18px",
                    flexGrow: 1,
                  }}
                >
                  {s.points.map((p) => (
                    <li
                      key={p}
                      style={{
                        fontSize: 13.5,
                        color: MUTED,
                        lineHeight: 1.5,
                        padding: "4px 0",
                      }}
                    >
                      <span style={{ color: SOFT_TX, fontWeight: 700 }}>✓</span>{" "}
                      {p}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={go}
                  style={{
                    alignSelf: "flex-start",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#fff",
                    color: INK,
                    border: `1px solid ${LINE}`,
                    borderRadius: 10,
                    padding: "10px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {s.cta.label} <ArrowRight size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── 함께 쓰면 좋은 서비스 (DocuAI 문서추출) — 하나의 계정 연동 ── */}
      <section style={{ ...container, paddingTop: 96 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 24,
            padding: "30px 32px",
            borderRadius: 18,
            background: `linear-gradient(150deg, ${TINT_DEEP} 0%, ${TINT} 100%)`,
            border: `1px solid ${SOFT_LINE}`,
          }}
        >
          <div style={{ maxWidth: 640 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.6,
                color: SOFT_TX,
                textTransform: "uppercase",
              }}
            >
              함께 쓰면 좋은 서비스
            </div>
            <div
              style={{
                fontSize: 21,
                fontWeight: 800,
                color: INK,
                marginTop: 8,
                letterSpacing: -0.3,
              }}
            >
              DocuAI — 물류·무역 문서 AI 추출
            </div>
            <p
              style={{
                fontSize: 15,
                color: BODY,
                marginTop: 10,
                lineHeight: 1.65,
              }}
            >
              인보이스·B/L·패킹리스트를 손으로 옮겨 적지 마세요. VLM이 표 구조까지
              인식해 엑셀·API로 바로 넘깁니다.{" "}
              <strong style={{ color: INK }}>하나의 계정</strong>으로 세번분류와
              문서추출을 함께 이용하세요.
            </p>
          </div>
          <a
            href="https://docuai.kr/"
            style={{
              ...primaryBtn,
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            DocuAI 문서추출 <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        style={{ ...container, padding: "84px 24px", textAlign: "center" }}
      >
        <h2
          style={{
            fontSize: 30,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: INK,
          }}
        >
          지금 바로 세번을 확인하세요
        </h2>
        <p style={{ fontSize: 16, color: MUTED, marginTop: 12 }}>
          신용카드 없이 · 설치 없이 · 바로 시작
        </p>
        <button
          onClick={onLoginClick}
          style={{
            ...primaryBtn,
            marginTop: 28,
            padding: "15px 30px",
            fontSize: 17,
          }}
        >
          로그인 / 시작하기 <ChevronRight size={18} />
        </button>
      </section>

      {/* ── 뉴스레터 구독 (listmonk 폼 POST — CORS 불필요 숨은 iframe) ── */}
      <NewsletterSignup container={container} />

      {/* ── 새소식 — 관세청 공지 큐레이션 (여기만 봐도 공지가 다 있다) ── */}
      <LandingNewsStrip container={container} />

      {/* ── Footer ── */}
      <footer
        style={{
          borderTop: `1px solid ${LINE}`,
          padding: "32px 0 40px",
          fontSize: 13,
          color: MUTED,
        }}
      >
        <div
          style={{
            ...container,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {/* 브랜드 + 링크 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <span>
              <span style={{ color: INK, fontWeight: 800 }}>LoginHS</span> — HS
              품목분류 세번 조회
            </span>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: "6px 12px",
              }}
            >
              {[
                ["개인정보처리방침", "https://docuai.tlogin.net/privacyPolicy"],
                ["이용약관", "https://docuai.tlogin.net/termsOfUse"],
                ["문의하기", "/?page=contact"],
                ["사용자 가이드", "/?page=guide"],
                ["모바일 앱 설치", "/?page=install"],
                ["API 문서", "/?page=api-docs"],
                ["운임 계산기(특송·과세운임·선편)", "/?page=freight"],
                ["수입 세금 계산기", "/?page=taxcalc"],
                ["관세환율", "/?page=exchange"],
                ["서식자료실", "/?page=forms"],
                ["새소식(관세청 공지)", "/?page=news"],
                ["홈페이지", "https://logins.kr/"],
              ].map(([t, href], i) => (
                <React.Fragment key={t}>
                  {i > 0 && <span style={{ color: "#d1d5db" }}>·</span>}
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    style={{
                      color: "#4b5563",
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    {t}
                  </a>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* 사업자 정보 */}
          <div
            style={{
              borderTop: `1px solid ${LINE}`,
              paddingTop: 14,
              display: "flex",
              flexWrap: "wrap",
              gap: "4px 10px",
              fontSize: 12.5,
              color: "#9aa0ad",
            }}
          >
            <span>(주)로그인네트웍</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span>대표 김정민</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span>사업자등록번호 121-86-03351</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span>통신판매 2019-인천중구-0407</span>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px 10px",
              fontSize: 12.5,
              color: "#9aa0ad",
            }}
          >
            <span>연락처 032-744-6000</span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span>인천광역시 중구 화랑목로 40-8 (운서동) 102·103·104호</span>
            <span style={{ marginLeft: "auto" }}>
              © {new Date().getFullYear()} loginHS
            </span>
          </div>
        </div>
      </footer>
      {amendmentPdf && (
        <AmendmentPdfViewerModal
          hsk="8501101000"
          item={amendmentPdf}
          onClose={() => setAmendmentPdf(null)}
        />
      )}
    </div>
  );
};

export default LandingPageLoginHS;
